import ls from 'localstorage-slim';
import React from 'react';
import {Tag} from 'antd';
import NameWithLink from '../../components/NameWithLink';
import StatusMark from '../../components/StatusMark';
import StatusLine from '../../components/StatusLine';
import {OptionsList} from './form.helpers';
import routes from '../../routes/routes.json';
import {vendorsPageConstants} from '../../pages/VendorsPage/vendorsPage.constants';
import {beneficiaryValidationResultStatuses, vendorPriorityConstants, vendorStatusConstants} from '../../constants';

const storedCardTableColumns = 'stored-vendors-table-columns-v2';

const vendorPriorityKeys = {
  [vendorPriorityConstants.OCCASIONAL]: 'vendorPriorities.occasional',
  [vendorPriorityConstants.STRATEGIC]: 'vendorPriorities.strategic',
  [vendorPriorityConstants.TACTICAL]: 'vendorPriorities.tactical',
};

const vendorStatuses = {
  [vendorStatusConstants.EMPTY]: {
    key: 'vendorStatuses.empty',
    color: '#B5B8BF'
  },
  [vendorStatusConstants.ACTIVE]: {
    key: 'vendorStatuses.active',
    color: '#52C41A'
  },
  [vendorStatusConstants.INACTIVE]: {
    key: 'vendorStatuses.inactive',
    color: '#00465D'
  },
  [vendorStatusConstants.TO_BE_VALIDATED]: {
    key: 'vendorStatuses.missingVAT',
    color: '#722ED1'
  },
};

/**
 * Returns a list of vendor priority select options with the given value marked active.
 * @param {string} activeValue - The currently selected priority value.
 * @returns {Array<{value: string, label: string}>} Array of priority option objects.
 * @example
 * const options = VendorPriorityOptions(vendorPriorityConstants.STRATEGIC);
 */
export const VendorPriorityOptions = (activeValue) => OptionsList(vendorPriorityKeys, activeValue);

/**
 * Returns a list of vendor status select options, each decorated with a color indicator element.
 * @param {boolean} [showOnlyVerified=false] - When true, returns only ACTIVE and INACTIVE statuses.
 * @returns {Array<{value: string, label: string, image: React.ReactElement}>} Array of status option objects.
 * @example
 * const allOptions = VendorStatusesOptions();
 * const verifiedOnly = VendorStatusesOptions(true);
 */
export const VendorStatusesOptions = (showOnlyVerified = false) => {
  const statuses = Object.keys(vendorStatuses).reduce((acc, value) => {
    acc[value] = vendorStatuses[value]?.key;
    return acc;
  }, {});

  const allowedStatuses = showOnlyVerified
    ? [vendorStatusConstants.ACTIVE, vendorStatusConstants.INACTIVE]
    : Object.values(vendorStatusConstants).filter(status => status !== vendorStatusConstants.EMPTY);

  return OptionsList(statuses).map(option => {
    let color = vendorStatuses[option.value]?.color;
    return {
      ...option,
      image: <StatusLine color={color} />
    }
  }).filter(status => allowedStatuses.includes(status.value));
}

/**
 * Collection of helper utilities for the Vendors domain.
 */
export const vendorsHelpers = {
  /**
   * Retrieves the persisted vendors table column configuration from local storage.
   * @returns {Array<string>|null} Stored column keys, or null if not set.
   * @example
   * const cols = vendorsHelpers.getStoredVendorsTableColumns();
   */
  getStoredVendorsTableColumns: () => ls.get(storedCardTableColumns),
  /**
   * Returns a NameWithLink element for the given vendor.
   * @param {Object|null} vendor - The vendor object.
   * @param {string} vendor.name - Display name of the vendor.
   * @param {string} [variant='system'] - Visual variant passed to NameWithLink.
   * @param {Function} [onClick] - When provided, the icon triggers this callback instead of navigating.
   * @param {string} linkIconTooltip - Display a tooltip message for link icon
   * @returns {React.ReactElement|null} A NameWithLink component, or null if vendor is falsy.
   * @example
   * const link = vendorsHelpers.getVendorNameWithLink(vendor, 'primary');
   */
  getVendorNameWithLink: ({vendor, variant = 'system', onClick, linkIconTooltip} = {}) => {
    if (!vendor) return null;
    const name = vendor?.name;
    const url = onClick ? undefined : vendorsHelpers.getVendorDetailsUrl(vendor);
    return <NameWithLink name={name} url={url} variant={variant} onClick={onClick} linkIconTooltip={linkIconTooltip} />;
  },
  /**
   * Returns the i18n key for a given vendor priority value.
   * @param {string} value - A vendorPriorityConstants value.
   * @returns {string|undefined} The translation key, or undefined if not found.
   * @example
   * const key = vendorsHelpers.getVendorPriority(vendorPriorityConstants.TACTICAL);
   */
  getVendorPriority: (value) => vendorPriorityKeys[value] || undefined,
  /**
   * Whether a vendor's status should render as an editable selector rather than a read-only mark.
   *
   * This used to be `getVendorStatusMarkOrSelector` and returned the `<VendorStatusSelector>`
   * element. That made this file import a component out of `components/pages/`, and because the
   * helpers barrel is imported eagerly by the app shell, it pulled that component into the entry
   * bundle for every user on every page. The decision stays here; the rendering moved to the two
   * call sites (H1MVP-4743).
   *
   * @param {Object} vendor - The vendor whose status is being shown.
   * @returns {boolean} True when the status is editable; false when it should be a status mark.
   * @example
   * vendorsHelpers.isVendorStatusEditable(vendor); // true
   */
  isVendorStatusEditable: (vendor) => (
    vendor?.status !== undefined
    && [vendorStatusConstants.ACTIVE, vendorStatusConstants.INACTIVE].includes(vendor.status)
  ),
  /**
   * Returns a StatusMark element for the given vendor's status.
   * @param {Object} vendor - The vendor object.
   * @param {string} vendor.status - The vendor's current status code.
   * @returns {React.ReactElement} A StatusMark component.
   * @example
   * const mark = vendorsHelpers.getVendorStatusMark(vendor);
   */
  getVendorStatusMark: (vendor) => (
    <StatusMark
      statusCode={vendor.status}
      statuses={vendorStatuses}
    />
  ),
  /**
   * Returns an array of Ant Design Tag elements for the given tag strings.
   * @param {string[]} [tags=[]] - Array of tag label strings.
   * @returns {React.ReactElement[]} Array of Tag components.
   * @example
   * const tagElements = vendorsHelpers.getVendorTags(['food', 'travel']);
   */
  getVendorTags: (tags = []) => tags.map((tag, index) => <Tag key={index}>{tag}</Tag>),
  /**
   * Persists the vendors table column configuration to local storage.
   * @param {Array<string>} value - Column keys to store.
   * @returns {void}
   * @example
   * vendorsHelpers.storeVendorsTableColumns(['name', 'status', 'priority']);
   */
  storeVendorsTableColumns: (value) => ls.set(storedCardTableColumns, value),
  /**
   * Returns stored column keys from local storage, or the provided defaults if none are stored.
   * @param {Array<string>} defaultKeys - Fallback column keys when nothing is stored.
   * @returns {Array<string>} Stored or default column keys.
   * @example
   * const cols = vendorsHelpers.getDefaultVendorsTableColumns(['name', 'status']);
   */
  getDefaultVendorsTableColumns: (defaultKeys) => vendorsHelpers.getStoredVendorsTableColumns() || defaultKeys,
  /**
   * Builds the vendor details page URL with id and name as query parameters.
   * @param {Object|null} vendor - The vendor object.
   * @param {string} vendor.vendor_id - Unique identifier of the vendor.
   * @param {string} vendor.name - Display name of the vendor.
   * @returns {string|null} The vendor details URL, or null if vendor_id is absent.
   * @example
   * const url = vendorsHelpers.getVendorDetailsUrl({ vendor_id: '123', name: 'Acme' });
   */
  getVendorDetailsUrl: (vendor) => {
    const id = vendor?.vendor_id;
    const name = vendor?.name;
    if (!id) return null;

    return `${routes.vendorsList}?${vendorsPageConstants.SELECTED_VENDOR_ID}=${id}&search=${name}`;
  },
  /**
   * Returns true if the vendor's status is TO_BE_VALIDATED.
   * @param {Object|null|undefined} vendor - The vendor object to check.
   * @returns {boolean} True if the vendor status is TO_BE_VALIDATED.
   * @example
   * if (vendorsHelpers.isVendorToBeValidated(vendor)) { ... }
   */
  isVendorToBeValidated: (vendor) => Boolean(vendor && vendor.status === vendorStatusConstants.TO_BE_VALIDATED),
  /**
   * Returns true if the vendor has an accounting external reference set.
   * @param {Object|null|undefined} vendor - The vendor object to check.
   * @returns {boolean} True if accounting_external_reference is present and truthy.
   * @example
   * if (vendorsHelpers.isVendorAccountingReference(vendor)) { ... }
   */
  isVendorAccountingReference: (vendor) => Boolean(vendor && vendor.accounting_external_reference),
  /**
   * Derives alert type, message, and validity from a beneficiary VOP validation result.
   * @param {Object} [options={}]
   * @param {Object} [options.validationDetails] - The raw validation details object from the API.
   * @param {Function} [options.t] - i18next translation function.
   * @returns {{isValid: boolean, alertType?: string, message?: string, beneficiaryValidationId?: string}}
   * @example
   * const details = vendorsHelpers.getBeneficiaryValidationDetails({ validationDetails, t });
   */
  getBeneficiaryValidationDetails: ({validationDetails, t} = {}) => {
    const beneficiaryValidationId = validationDetails?.beneficiary_validation_id;
    if (!validationDetails || !validationDetails.vop) {
      return {isValid: false, beneficiaryValidationId};
    }

    const {matched_name, result} = validationDetails.vop;

    let alertType = 'warning';
    let message = '';

    switch (result) {
      case beneficiaryValidationResultStatuses.MATCH:
        message = t(`vendors:beneficiaryValidationResults.${beneficiaryValidationResultStatuses.MATCH}`);
        alertType = 'success';
        break;
      case beneficiaryValidationResultStatuses.CLOSE_MATCH:
        message = t(`vendors:beneficiaryValidationResults.${beneficiaryValidationResultStatuses.CLOSE_MATCH}`, {matched_name});
        break;
      case beneficiaryValidationResultStatuses.NO_MATCH:
        message = t(`vendors:beneficiaryValidationResults.${beneficiaryValidationResultStatuses.NO_MATCH}`);
        break;
      case beneficiaryValidationResultStatuses.VERIFICATION_POSSIBLE:
        message = t(`vendors:beneficiaryValidationResults.${beneficiaryValidationResultStatuses.VERIFICATION_POSSIBLE}`);
        break;
      default:
        message = t('vendors:beneficiaryValidationResults.unknownValidationResult');
        break;
    }
    return { isValid: true, alertType, message, beneficiaryValidationId };
  }
}
